/**
 * @license
 * @copyright Copyright Motili Inc., 2024 All Rights Reserved
 */

import { CSSProperties, HTMLAttributeAnchorTarget, ReactNode } from 'react';
import { LabelWithRequired, joinDataTestId } from '@motili/ui';

import { generateDefaultDataTestId } from 'common/utils/Utils';
import MotiliTextButton from 'common/components/buttons/MotiliTextButton';

import styles from 'common/components/form/FormTextView.module.scss';
import { useUI } from 'common/context/UIContext';

interface FormTextViewButton {
    text: ReactNode;
    onClick?: () => void;
    color?: string;
    disabled?: boolean;
}

interface FormTextViewProps {
    label?: string;
    value?: ReactNode;
    button?: FormTextViewButton;
    style?: CSSProperties;
    labelStyle?: CSSProperties;
    messageStyle?: CSSProperties;
    showAsLink?: boolean;
    urlDestination?: string;
    bypassPreTag?: boolean;
    linkTarget?: HTMLAttributeAnchorTarget;
    required?: boolean;
    disabled?: boolean;
    emptyDisplay?: string;
    dataTestId?: string;
    preserveCase?: boolean;
}

const FormTextView = ({
    label,
    value,
    button,
    showAsLink,
    urlDestination,
    style,
    labelStyle,
    messageStyle,
    bypassPreTag,
    linkTarget,
    required,
    emptyDisplay = '',
    dataTestId = '',
    preserveCase,
}: FormTextViewProps) => {
    const { isLegacyUI } = useUI();
    const isDisplayAsLegacyUI = !preserveCase && isLegacyUI;
    const transformedEmptyDisplay = isDisplayAsLegacyUI
        ? emptyDisplay || '<BLANK>'
        : emptyDisplay;
    return (
        <>
            <div className={styles.labelContainer}>
                <div>
                    {label &&
                        (labelStyle ? (
                            <label data-test-id='label' style={labelStyle}>
                                {isDisplayAsLegacyUI
                                    ? label.toUpperCase()
                                    : label}
                                {required && (
                                    <span style={{ color: 'red' }}> *</span>
                                )}
                            </label>
                        ) : (
                            <LabelWithRequired
                                label={
                                    isDisplayAsLegacyUI
                                        ? label.toUpperCase()
                                        : label
                                }
                                required={required}
                                dataTestId={dataTestId}
                            />
                        ))}
                </div>
                {button && (
                    <MotiliTextButton
                        disabled={button.disabled}
                        text={button.text}
                        onClick={button.onClick}
                        color={button.color}
                        data-test-id={dataTestId}
                    />
                )}
            </div>
            {showAsLink && (
                <div className={styles.value} style={style}>
                    {value ? (
                        <a
                            target={linkTarget || '_self'}
                            href={urlDestination}
                            data-test-id={
                                joinDataTestId([dataTestId, 'link_value']) ||
                                generateDefaultDataTestId(label, 'link')
                            }
                        >
                            {value}
                        </a>
                    ) : (
                        transformedEmptyDisplay
                    )}
                </div>
            )}

            {(bypassPreTag === undefined || bypassPreTag === false) &&
                !showAsLink && (
                    <div className={styles.value} style={style}>
                        <pre
                            data-test-id={joinDataTestId([dataTestId, 'value'])}
                            className={styles.message}
                            style={messageStyle}
                        >
                            {value === 0
                                ? value
                                : value || transformedEmptyDisplay}
                        </pre>
                    </div>
                )}
            {bypassPreTag && !showAsLink && (
                <div className={styles.value} style={style}>
                    <div data-test-id={joinDataTestId([dataTestId, 'value'])}>
                        {value === 0 ? value : value || transformedEmptyDisplay}
                    </div>
                </div>
            )}
        </>
    );
};

export default FormTextView;
